
import React, { useEffect, useState, useMemo, useRef } from 'react';
import { getCampus, deleteCampus } from '../../api/campus';
import styles from '../../styles/MateriasStyles/PantallaDeTareas.v2.module.css';
import Pagination from '../../components/Pagination.tsx';
import { usePermissions } from '../../hooks/usePermissions';


function CampusTable({ onEdit, onLoadingChange }) {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [page, setPage] = useState(1);
  const pageSize = 8;
  const filtered = useMemo(() => data, [data]);
  const totalPages = Math.max(1, Math.ceil(filtered.length / pageSize));
  const paginated = filtered.slice((page - 1) * pageSize, page * pageSize);

  // Crear clave única basada en IDs para detectar cambios reales
  const dataKey = useMemo(() => {
    return data.map(d => d.id_campus || d.id).sort().join(',');
  }, [data]);

  const prevDataKeyRef = useRef(dataKey);

  // Reiniciar página solo cuando realmente cambia la lista
  useEffect(() => {
    if (prevDataKeyRef.current !== dataKey) {
      setPage(1);
      prevDataKeyRef.current = dataKey;
    }
  }, [dataKey]);

  // Ajustar página si quedó fuera de rango
  useEffect(() => {
    if (page > totalPages && totalPages > 0) {
      setPage(totalPages);
    }
  }, [page, totalPages]);
  
  // Permisos del módulo Catálogos
  const { canEdit, canDelete } = usePermissions();
  const puedeEditar = canEdit('Catálogos');
  const puedeEliminar = canDelete('Catálogos');

  const fetchData = async () => {
    setLoading(true);
    onLoadingChange?.(true);
    try {
      const campus = await getCampus();
      setData(campus);
      setError(null);
    } catch (err) {
      setError('Error al cargar campus');
    } finally {
      setLoading(false);
      onLoadingChange?.(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  const handleDelete = async (id) => {
    if (!window.confirm('¿Eliminar este campus?')) return;
    try {
      await deleteCampus(id);
      fetchData();
    } catch {
      alert('Error al eliminar campus');
    }
  };

  const ActionButtons = ({ row }) => (
    <div className={styles.actionButtons}>
      {puedeEditar && (
        <button aria-label="Editar" className={styles.actionButton} onClick={() => onEdit?.(row)}>
          <img
            src="https://api.builder.io/api/v1/image/assets/189f954e013f4fa084ccafbef336b287/83eacec9f1f974b33615bbc95aea7ff7b2d13b0e?placeholderIfAbsent=true"
            alt="Editar"
            className={styles.actionIcon}
          />
        </button>
      )}
      {puedeEliminar && (
        <button aria-label="Eliminar" className={styles.actionButton} onClick={() => handleDelete(row.id)}>
          <img
            src="https://api.builder.io/api/v1/image/assets/189f954e013f4fa084ccafbef336b287/bef20ed4bd5293db48e37df8e511dca0fed7db2e?placeholderIfAbsent=true"
            alt="Eliminar"
            className={styles.actionIcon}
          />
        </button>
      )}
      {!puedeEditar && !puedeEliminar && (
        <span style={{ color: '#999', fontSize: '12px' }}>Solo lectura</span>
      )}
    </div>
  );

  const Header = () => (
    <div className={styles.tableHeader}>
      <div className={styles.tableTitle}>
        <div className={styles.tableTitleIcon}>
          <div className={styles.iconContainerOrange}>
            <span>🏫</span>
          </div>
        </div>
        <h2 className={styles.tableTitleText}>Campus e Información Bancaria</h2>
      </div>
    </div>
  );

  if (loading) {
    return (
      <div className={styles.tableContainer} style={{ position: 'relative', zIndex: 1 }}>
        <Header />
      </div>
    );
  }

  return (
    <div className={styles.tableContainer} style={{ position: 'relative', minHeight: '320px', zIndex: 1 }}>
      <Header />

      <div className={styles.tableHeaderRow} style={{ gridTemplateColumns: 'minmax(210px,1fr) minmax(120px,0.8fr) minmax(140px,1fr) minmax(120px,0.7fr) minmax(120px,0.7fr)' }}>
        <span className={styles.columnHeader}>Nombre del Campus</span>
        <span className={styles.columnHeader}>Código</span>
        <span className={styles.columnHeader}>Teléfono</span>
        <span className={styles.columnHeader}>Estado</span>
        <span className={styles.columnHeader + ' ' + styles.stickyActionsHeader}>Acciones</span>
      </div>

      <div className={styles.tableBody}>
        {error ? (
          <div className={styles.tableRow} style={{ gridTemplateColumns: 'minmax(210px,1fr) minmax(120px,0.8fr) minmax(140px,1fr) minmax(120px,0.7fr) minmax(120px,0.7fr)' }}>
            <span className={styles.cellData} colSpan={5}>{error}</span>
          </div>
        ) : paginated.length === 0 ? (
          <div className={styles.tableRow} style={{ gridTemplateColumns: 'minmax(210px,1fr) minmax(120px,0.8fr) minmax(140px,1fr) minmax(120px,0.7fr) minmax(120px,0.7fr)' }}>
            <span className={styles.cellData} colSpan={5}>No hay campus registrados</span>
          </div>
        ) : paginated.map((row, idx) => (
          <div key={row.id || idx} className={styles.tableRow} style={{ gridTemplateColumns: 'minmax(210px,1fr) minmax(120px,0.8fr) minmax(140px,1fr) minmax(120px,0.7fr) minmax(120px,0.7fr)' }}>
            <span className={styles.cellData}>{row.nombre || '—'}</span>
            <span className={styles.cellData}>{row.codigo || '—'}</span>
            <span className={styles.cellData}>{row.telefono || '—'}</span>
            <div className={styles.statusCell}>
              <div className={row.estado === 'ACTIVO' ? styles.statusActive : styles.statusInactive}>
                <span>{row.estado || 'INACTIVO'}</span>
              </div>
            </div>
            <div className={styles.actionButtons + ' ' + styles.stickyActionsCell}>
              <ActionButtons row={row} />
            </div>
          </div>
        ))}
      </div>

      {/* Paginación global */}
      {totalPages > 1 && (
        <Pagination
          page={page}
          totalPages={totalPages}
          onPageChange={setPage}
          className={styles.paginationWrapper}
        />
      )}
    </div>
  );
}

export default CampusTable;
